import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { cn } from "../lib/utils";

// Haptic feedback utility
function hapticFeedback(duration: number = 15) {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    navigator.vibrate(duration);
  }
}

export interface FABProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  position?: "bottom-right" | "bottom-center";
  size?: "default" | "lg";
  showLabel?: boolean;
}

const FAB = React.forwardRef<HTMLButtonElement, FABProps>(
  (
    {
      icon: Icon,
      label,
      position = "bottom-right",
      size = "default",
      showLabel = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      hapticFeedback();
      onClick?.(e);
    };
    const positionClasses = {
      "bottom-right": "bottom-24 right-4 lg:bottom-6 lg:right-6",
      "bottom-center": "bottom-24 left-1/2 -translate-x-1/2 lg:bottom-6",
    };

    const sizeClasses = {
      default: "h-14 w-14",
      lg: "h-16 w-16",
    };

    return (
      <button
        ref={ref}
        onClick={handleClick}
        className={cn(
          "fixed z-40 flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl active:scale-95",
          positionClasses[position],
          showLabel ? "px-6" : sizeClasses[size],
          className
        )}
        aria-label={label}
        {...props}
      >
        <Icon className={cn("h-6 w-6", size === "lg" && "h-7 w-7")} />
        {showLabel && <span className="font-medium">{label}</span>}
      </button>
    );
  }
);
FAB.displayName = "FAB";

export { FAB };
